import { useMemo, useState } from "react";
import type { Key } from "react-aria-components";
import { Area, AreaChart, CartesianGrid, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import type { PartnerHomeResponse } from "@mfa-aff/shared";
import { ChartFrame, formatJalali, formatNumber, formatRatio, jalaliTick, rialToChartToman, tomanTick } from "@mfa-aff/ui";
import { ChartLegendContent, ChartTooltipContent } from "@mfa-aff/ui/components/application/charts/charts-base";
import { MetricChangeIndicator } from "@mfa-aff/ui/components/application/metrics/metrics";
import { ButtonGroup, ButtonGroupItem } from "@mfa-aff/ui/components/base/button-group/button-group";

type Range = "30" | "90";

/** 30/90-day commission chart (Untitled UI Dashboard 01 area chart pattern, inside an LTR island). */
export const EarningsChart = ({ series: raw }: { series: PartnerHomeResponse["series"] }) => {
    const [range, setRange] = useState<Range>("30");
    const days = Number(range);
    // Rial strings from the API → whole Toman numbers for the chart only (display stays Toman).
    const series = useMemo(
        () =>
            raw.days.map((d) => ({
                date: d.date,
                newToman: rialToChartToman(d.newRial),
                renewalToman: rialToChartToman(d.renewalRial),
                legacyNewToman: rialToChartToman(d.legacyNewRial ?? "0"),
                legacyRenewalToman: rialToChartToman(d.legacyRenewalRial ?? "0"),
            })),
        [raw.days],
    );

    const { data, total, change, hasLegacy } = useMemo(() => {
        const data = series.slice(-days);
        const sum = (rows: typeof series) => rows.reduce((acc, r) => acc + r.newToman + r.renewalToman + r.legacyNewToman + r.legacyRenewalToman, 0);
        const total = sum(data);
        const prev = sum(series.slice(-days * 2, -days));
        const change = prev > 0 && series.length >= days * 2 ? (total - prev) / prev : undefined;
        const hasLegacy = data.some((r) => r.legacyNewToman !== 0 || r.legacyRenewalToman !== 0);
        return { data, total, change, hasLegacy };
    }, [series, days]);

    return (
        <ChartFrame
            title="پورسانت ثبت‌شده"
            description={
                hasLegacy
                    ? "جمع پورسانت فروش جدید و تمدید بر اساس روز پرداخت فاکتور؛ رنگ‌های روشن، روزهای پیش از این پنل در سیستم قبلی‌اند."
                    : "جمع پورسانت فروش جدید و تمدید، بر اساس روز پرداخت فاکتور"
            }
            height={280}
            stretch
            className="rounded-2xl"
            actions={
                <ButtonGroup
                    size="sm"
                    selectedKeys={new Set<Key>([range])}
                    onSelectionChange={(keys) => {
                        const next = [...keys][0];
                        if (next) setRange(String(next) as Range);
                    }}
                    disallowEmptySelection
                >
                    <ButtonGroupItem id="30">۳۰ روز</ButtonGroupItem>
                    <ButtonGroupItem id="90">۹۰ روز</ButtonGroupItem>
                </ButtonGroup>
            }
            legend={
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <span className="text-display-xs font-semibold whitespace-nowrap text-primary digits-tabular">
                            {formatNumber(total)} <span className="text-sm font-medium text-tertiary">تومان</span>
                        </span>
                        {change !== undefined && (
                            <MetricChangeIndicator
                                type="modern"
                                trend={change >= 0 ? "positive" : "negative"}
                                value={`${formatRatio(Math.abs(change))} نسبت به ${days} روز قبل`}
                            />
                        )}
                    </div>
                    <ChartLegendContent
                        payload={[
                            { value: "فروش جدید", type: "circle", color: "currentColor", payload: { className: "text-utility-brand-600" } },
                            { value: "تمدید", type: "circle", color: "currentColor", payload: { className: "text-accent-500" } },
                            ...(hasLegacy
                                ? [
                                      { value: "فروش جدید · سیستم قبلی", type: "circle" as const, color: "currentColor", payload: { className: "text-utility-brand-300" } },
                                      { value: "تمدید · سیستم قبلی", type: "circle" as const, color: "currentColor", payload: { className: "text-accent-300" } },
                                  ]
                                : []),
                        ]}
                    />
                </div>
            }
        >
            <AreaChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 0 }} className="text-tertiary [&_.recharts-text]:text-xs">
                <defs>
                    <linearGradient id="grad-new" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="currentColor" className="text-utility-brand-600" stopOpacity="0.3" />
                        <stop offset="95%" stopColor="currentColor" className="text-utility-brand-600" stopOpacity="0.02" />
                    </linearGradient>
                    <linearGradient id="grad-renewal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="currentColor" className="text-accent-500" stopOpacity="0.3" />
                        <stop offset="95%" stopColor="currentColor" className="text-accent-500" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="currentColor" className="text-utility-neutral-100" />
                <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tickMargin={10}
                    minTickGap={28}
                    fill="currentColor"
                    tickFormatter={jalaliTick("dayMonth")}
                />
                <YAxis
                    orientation="right"
                    axisLine={false}
                    tickLine={false}
                    width={80}
                    fill="currentColor"
                    tickFormatter={tomanTick}
                />
                <RechartsTooltip
                    content={<ChartTooltipContent />}
                    formatter={(value) => `${formatNumber(Number(value))} تومان`}
                    labelFormatter={(label) => formatJalali(String(label), "weekday")}
                    cursor={{ className: "stroke-utility-brand-600 stroke-2" }}
                />
                {hasLegacy && (
                    <Area
                        isAnimationActive={false}
                        dataKey="legacyRenewalToman"
                        name="تمدید · سیستم قبلی"
                        stackId="c"
                        type="monotone"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        className="text-accent-300"
                        fill="currentColor"
                        fillOpacity={0.12}
                        activeDot={{ className: "fill-bg-primary stroke-accent-300 stroke-2" }}
                    />
                )}
                {hasLegacy && (
                    <Area
                        isAnimationActive={false}
                        dataKey="legacyNewToman"
                        name="فروش جدید · سیستم قبلی"
                        stackId="c"
                        type="monotone"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        className="text-utility-brand-300"
                        fill="currentColor"
                        fillOpacity={0.12}
                        activeDot={{ className: "fill-bg-primary stroke-utility-brand-300 stroke-2" }}
                    />
                )}
                <Area
                    isAnimationActive={false}
                    dataKey="renewalToman"
                    name="تمدید"
                    stackId="c"
                    type="monotone"
                    stroke="currentColor"
                    strokeWidth={2}
                    className="text-accent-500"
                    fill="url(#grad-renewal)"
                    activeDot={{ className: "fill-bg-primary stroke-accent-500 stroke-2" }}
                />
                <Area
                    isAnimationActive={false}
                    dataKey="newToman"
                    name="فروش جدید"
                    stackId="c"
                    type="monotone"
                    stroke="currentColor"
                    strokeWidth={2}
                    className="text-utility-brand-600"
                    fill="url(#grad-new)"
                    activeDot={{ className: "fill-bg-primary stroke-utility-brand-600 stroke-2" }}
                />
            </AreaChart>
        </ChartFrame>
    );
};
